import { useContext, useEffect, useState } from "react";
import { updateAdmin } from "../../context/AdministratorsContext/Actions";
import AdministratorsContext from "../../context/AdministratorsContext/AdministratorsContext";
import { styled } from "@mui/system";
import SwitchUnstyled, {
  switchUnstyledClasses,
} from "@mui/base/SwitchUnstyled";
import { useMutation, useApolloClient } from "@apollo/client";
import { UPDATE_USERS_PERMISSIONS, GET_PERMISSIONS_USER, GET_PERMISSIONS_USER_PANEL } from "../../Querys/querys";
import { AdminContext } from "../../context/AdminContext/AdminContext";
import { getDescendantPermissionNames, protegerAutobloqueo } from "../../helpers/Administradores/permissionCascade";
import CircularProgress from "@mui/material/CircularProgress";

const blue = {
  500: "#11ca73",
};

const grey = {
  400: "#BFC7CF",
  500: "#AAB4BE",
  600: "#6F7E8C",
};

const Root = styled("span")(
  ({ theme }) => `
  font-size: 0;
  position: relative;
  display: inline-block;
  width: 27px;
  height: 12px;
  padding: 2px 3px 4px 5px;
  cursor: pointer;

  &.${switchUnstyledClasses.disabled} {
    opacity: 0.4;
    cursor: not-allowed;
  }

  & .${switchUnstyledClasses.track} {
    background: ${theme.palette.mode === "dark" ? grey[600] : grey[400]};
    border-radius: 10px;
    display: block;
    height: 100%;
    width: 100%;
    position: absolute;
  }

  & .${switchUnstyledClasses.thumb} {
    display: block;
    width: 11px;
    height: 11px;
    top: 3px;
    left: 3px;
    border-radius: 16px;
    background-color: #fff;
    position: relative;
    transition: all 200ms ease;
  }

  &.${switchUnstyledClasses.focusVisible} .${switchUnstyledClasses.thumb} {
    background-color: ${grey[500]};
    box-shadow: 0 0 1px 8px rgba(0, 0, 0, 0.25);
  }

  &.${switchUnstyledClasses.checked} {
    .${switchUnstyledClasses.thumb} {
      left: 22px;
      top: 3px;
      background-color: #fff;
    }

    .${switchUnstyledClasses.track} {
      background: ${blue[500]};
    }
  }

  & .${switchUnstyledClasses.input} {
    cursor: inherit;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    opacity: 0;
    z-index: 1;
    margin: 0;
  }d
  `
);

const Switch = (props: any) => {
  const label = { componentsProps: { input: { "aria-label": "Demo switch" } } };
  const { state, dispatch } = useContext(AdministratorsContext);
  const { adminState } = useContext(AdminContext)
  // Valor resuelto: props.Value puede venir como thunk (() => handleConfirmPermission(...)) o booleano.
  const resolved =
    typeof props?.Value === "function" ? props.Value() : props?.Value;
  const [value, setValue] = useState(Boolean(resolved));

  // Reactividad: reflejar el valor fresco tras el refetch de permisos (DBSB-458).
  // Sin esto, el useState lazy-init nunca resincroniza y la cascada no se pinta.
  useEffect(() => {
    setValue(Boolean(resolved));
  }, [resolved]);

  const [updatePermission] = useMutation(UPDATE_USERS_PERMISSIONS);
  const client = useApolloClient();
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = async (event: any) => {
    if (isSaving) return; // evitar doble disparo mientras cascadea
    const nuevoStatus = !value;
    setValue(nuevoStatus);
    setIsSaving(true);
    try {
    // Cascada padre -> hijos genérica por jerarquía de nombre (DBSB-458):
    // todo permiso "Padre.*" hereda el estado del padre. Uniforme para TODOS los
    // módulos sin cablear cada switch; la lista completa viene del contexto (PANEL).
    // getDescendantPermissionNames mapea también el principal "Sidebar.<Modulo>"
    // al subárbol "<Modulo>.*".
    const hijos = getDescendantPermissionNames(props.NombreCampo, state?.Permisos);
    // Salvaguarda anti-autobloqueo (DBSB-458): al editar tu PROPIA cuenta, la cascada
    // nunca apaga el permiso que gatea esta pantalla — perderías el acceso al panel y
    // no podrías revertirlo desde la UI.
    const editandoPropiaCuenta =
      adminState?.id != null && String(state?._id) === String(adminState.id);
    const nombres = protegerAutobloqueo([props.NombreCampo, ...hijos], {
      apagando: nuevoStatus === false,
      editandoPropiaCuenta,
    });

    // Persistir padre + hijos con el mismo estado, en paralelo (un subárbol puede
    // tener 30+ permisos). Sin refetch por-llamada.
    await Promise.all(
      nombres.map((nombre) =>
        updatePermission({
          variables: {
            id: state?._id,
            input: {
              name: nombre,
              status: nuevoStatus,
            },
          },
          refetchQueries: [],
        })
      )
    );

    // Un único refetch al final, ya con todos los writes confirmados, para
    // resincronizar la UI (los switches/checkboxes reactivos se repintan).
    await client.refetchQueries({
      include: [GET_PERMISSIONS_USER, GET_PERMISSIONS_USER_PANEL],
    });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <SwitchUnstyled
        checked={value}
        onChange={handleChange}
        component={Root}
        disabled={isSaving}
        {...label}
      />
      {isSaving && <CircularProgress size={12} thickness={5} />}
    </div>
  );
};

export default Switch;
